import { useId, type ReactNode, type SelectHTMLAttributes, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';

export interface FormFieldProps {
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  children: (props: { id: string; describedBy: string | undefined }) => ReactNode;
}

/** Label, hint, error and the aria wiring. Every input in the product sits inside one. */
export function FormField({ label, hint, error, required = false, children }: FormFieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="o-field">
      <label className="o-field__label" htmlFor={id}>
        {label}
        {required ? ' (required)' : ''}
      </label>
      {children({ id, describedBy })}
      {hint ? (
        <span className="o-field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="o-field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string;
  hint?: string;
  error?: string | null;
  mono?: boolean;
}

export function TextInput({ label, hint, error, mono, required, ...rest }: TextInputProps) {
  return (
    <FormField
      label={label}
      {...(hint ? { hint } : {})}
      error={error ?? null}
      required={required === true}
    >
      {({ id, describedBy }) => (
        <input
          {...rest}
          id={id}
          className={mono ? 'o-input o-input--mono' : 'o-input'}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          required={required}
        />
      )}
    </FormField>
  );
}

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  label: string;
  hint?: string;
  error?: string | null;
}

export function TextArea({ label, hint, error, required, ...rest }: TextAreaProps) {
  return (
    <FormField
      label={label}
      {...(hint ? { hint } : {})}
      error={error ?? null}
      required={required === true}
    >
      {({ id, describedBy }) => (
        <textarea
          {...rest}
          id={id}
          className="o-textarea"
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          required={required}
        />
      )}
    </FormField>
  );
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  label: string;
  hint?: string;
  error?: string | null;
  options: readonly { value: string; label: string }[];
}

export function SelectField({ label, hint, error, options, required, ...rest }: SelectFieldProps) {
  return (
    <FormField
      label={label}
      {...(hint ? { hint } : {})}
      error={error ?? null}
      required={required === true}
    >
      {({ id, describedBy }) => (
        <select
          {...rest}
          id={id}
          className="o-select"
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          required={required}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FormField>
  );
}

/**
 * The reason box every tier 2 action needs. Separate from TextArea so the copy and the
 * required rule are identical everywhere, and so a reviewer can grep for it.
 */
export function ReasonField(props: {
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
}) {
  return (
    <TextArea
      label="Reason"
      hint="Recorded in the audit row and mirrored to Slack."
      required
      value={props.value}
      error={props.error ?? null}
      onChange={(event) => props.onChange(event.target.value)}
    />
  );
}
